import type { CosmeticMap, MakeupStyle, StyleKit, StyleRationale } from '../catalog/catalog';
import type { VtoLang } from './vtoCopy';

type LocalizedKit = {
  lipstickNote: string;
  blushNote: string;
  eyeshadowNote: string;
};

type LocalizedStyleCopy = {
  tagline: string;
  kit?: LocalizedKit;
  rationale?: StyleRationale;
  cosmeticMap?: CosmeticMap;
};

const en: Record<string, LocalizedStyleCopy> = {
  'forever-young': {
    tagline: 'Baby gloss, your first lipstick',
    kit: {
      lipstickNote: 'Baby gloss: on cool skin it shifts toward neutral pink (#f0a5b2), on warm skin toward peach (#f4a89c).',
      blushNote: 'Creamy peach - liquid freshness, not an obvious color statement.',
      eyeshadowNote: 'Very light pearl champagne. Light, not shadow.'
    },
    rationale: {
      whyItWorks: 'The gloss is youthful, so it cannot compete with a heavy eye. Champagne pearl adds brightness without drama. Peach blush keeps the face fresh and effortless.',
      skinFit: 'Universal, especially good on fresh young skin. On mature skin the baby gloss can feel out of context; consider a more pigmented satin.',
      occasionFit: 'Daytime, school, first date, afternoon. Not evening, not events.',
      avoid: 'Dark matte shadow, sharp lip liner, smokey eye. The look is lightness; anything heavy breaks it.'
    },
    cosmeticMap: {
      intent: 'Fresh gloss with a young, lightweight effect',
      focus: ['lips', 'skin'],
      paletteFamily: 'rose nude / champagne',
      temperature: 'neutral-warm',
      contrast: 'low',
      chroma: 'soft',
      skinDepthFit: 'fair-light-medium',
      finishProfile: { skin: 'natural glow', lips: 'glossy sheer', eyes: 'soft shimmer', cheeks: 'cream diffused' },
      placement: { eyes: 'very light mobile lid, nearly transparent crease', cheeks: 'soft apples with short drape', lips: 'soft edge, no graphic liner' },
      counterBrief: ['smokey eye', 'dark matte', 'defined lip contour'],
      riskControls: ['cap light shimmer', 'mandatory crease fade', 'soft lip edge']
    }
  },
  'millennial-rose': {
    tagline: 'Dusty rose, the common denominator',
    kit: {
      lipstickNote: 'Universal dusty tone; on very deep skin it can flatten, shift to berry rose (#a5636a).',
      blushNote: 'Slightly satin dusty rose - same tone, just warmer.',
      eyeshadowNote: 'Matte rosy taupe. Tone-on-tone, zero contrast.'
    },
    rationale: {
      whyItWorks: 'Dusty monochrome. Rose repeats across lips, cheeks and a taupe-shifted eye. Coherent without becoming dull; the look that never misses.',
      skinFit: 'Almost universal, excellent on medium neutral skin. On deep skin it needs more contrast: shift the lipstick to berry rose.',
      occasionFit: 'Office, meetings, work coffee, any daytime moment. The safest look in the catalog.',
      avoid: 'Intense shimmer, graphic black liner, bright blush. Any accent breaks the tonal whisper.'
    },
    cosmeticMap: {
      intent: 'Universal dusty rose monochrome',
      focus: ['balanced'],
      paletteFamily: 'dusty rose',
      temperature: 'neutral',
      contrast: 'low-medium',
      chroma: 'muted',
      skinDepthFit: 'light-medium',
      finishProfile: { skin: 'natural satin', lips: 'satin', eyes: 'matte taupe rose', cheeks: 'satin diffuse' },
      placement: { eyes: 'rosy taupe close to the crease', cheeks: 'wide, soft cheek wash', lips: 'natural satin edge' },
      counterBrief: ['intense shimmer', 'graphic black liner', 'bright blush'],
      riskControls: ['preserve muted chroma', 'avoid overcontrast', 'soft cheek edge']
    }
  },
  'bare-hours': {
    tagline: 'Matte nude for the hours that matter',
    kit: {
      lipstickNote: 'Your nude has to adapt to your skin. Cool: mauve (#b08882). Warm: keep this. Deep: deeper caramel (#9a6b52) or cinnamon (#7a4b38).',
      blushNote: 'Natural rosy nude - not color, just health.',
      eyeshadowNote: 'Neutral matte taupe. The eye is present without speaking.'
    },
    rationale: {
      whyItWorks: 'Everything matte, everything neutral. No distraction, maximum authority. The blush is not color, only circulation. The message is: remember what I say, not how I look.',
      skinFit: 'The right nude depends on skin depth; if it is not adapted, it drains the face. The lipstick note is the key detail of the whole look.',
      occasionFit: 'Important meeting, presentation, pitch. Hours when you need to be remembered for the content, not the styling.',
      avoid: 'Gloss, shimmer, color accents. Add color and it becomes another look.'
    },
    cosmeticMap: {
      intent: 'Clean, professional, authoritative nude',
      focus: ['skin', 'lips'],
      paletteFamily: 'nude caramel',
      temperature: 'neutral-warm',
      contrast: 'low',
      chroma: 'muted',
      skinDepthFit: 'light-medium-tan',
      finishProfile: { skin: 'matte natural', lips: 'matte nude', eyes: 'matte neutral', cheeks: 'satin skinlike' },
      placement: { eyes: 'minimal definition on the mobile lid', cheeks: 'natural health, not color', lips: 'clean edge without dark contour' },
      counterBrief: ['gloss', 'shimmer', 'color accents'],
      riskControls: ['adapt nude to depth', 'avoid grey nude', 'keep opacity moderate']
    }
  },
  'soft-focus': {
    tagline: 'Minimal mauve, clean line',
    kit: {
      lipstickNote: 'Cool tone, ideal for neutral or cool undertones. On warm skin it can look ashy; shift to a warmer rose mauve (#b18686).',
      blushNote: 'Cool mauve in a sheer veil. Soft-spoken color, not full color.',
      eyeshadowNote: 'Cool matte taupe. Definition without harshness.'
    },
    rationale: {
      whyItWorks: 'A coherent cool palette from lip to eye. Mauve cleans the face; nothing competes. Minimal does not mean anonymous, it means intentional.',
      skinFit: 'Cool and neutral skin. On warm skin, move everything half a tone warmer.',
      occasionFit: 'Creative days, galleries, studios, conceptual spaces. When you want to say you thought about what you wear.',
      avoid: 'Warm bronze tones, gold shimmer, coral blush. They break the cool cleanliness of the archetype.'
    },
    cosmeticMap: {
      intent: 'Cool minimalist mauve with a clean line',
      focus: ['balanced', 'lips'],
      paletteFamily: 'cool mauve',
      temperature: 'cool-neutral',
      contrast: 'medium-low',
      chroma: 'muted',
      skinDepthFit: 'fair-light-medium',
      finishProfile: { skin: 'soft matte', lips: 'matte mauve', eyes: 'cool matte taupe', cheeks: 'sheer mauve' },
      placement: { eyes: 'thin cool shadow above the lash line', cheeks: 'low-saturation veil', lips: 'soft matte edge' },
      counterBrief: ['warm bronze', 'gold shimmer', 'coral'],
      riskControls: ['warm skin adjustment', 'avoid ashy cast', 'low blush saturation']
    }
  },
  'cherry-pop': {
    tagline: 'Glossy cherry, pure energy',
    kit: {
      lipstickNote: 'Bright cherry suits every skin; on very fair skin shift to a warmer pink-red (#dc305a).',
      blushNote: 'Warm pink with a coral hint - a veil, not full color.',
      eyeshadowNote: 'Discreet champagne shimmer. The eye is light, not shadow.'
    },
    rationale: {
      whyItWorks: 'Cherry gloss is playful: the eye should be bright, not dramatic. Champagne shimmer keeps it lifted, while warm pink blush ties the lipstick back to the face.',
      skinFit: 'Universal; cherry gloss is democratic and forgiving across undertones.',
      occasionFit: 'Weekend, brunch, birthdays, days when energy is the right word.',
      avoid: 'Smokey, heavy matte, evening eye. Cherry Pop lives by day and fades at night.'
    },
    cosmeticMap: {
      intent: 'Energetic, bright cherry gloss',
      focus: ['lips'],
      paletteFamily: 'bright cherry / champagne',
      temperature: 'neutral-cool',
      contrast: 'medium-high',
      chroma: 'bright',
      skinDepthFit: 'universal',
      finishProfile: { skin: 'fresh glow', lips: 'glossy bright', eyes: 'champagne shimmer', cheeks: 'sheer warm pink' },
      placement: { eyes: 'champagne light, no depth', cheeks: 'light warm pink', lips: 'controlled glossy edge' },
      counterBrief: ['smokey', 'heavy matte', 'evening eye'],
      riskControls: ['cap gloss highlight', 'keep eyes secondary', 'avoid red bleed']
    }
  },
  'sunset-riot': {
    tagline: 'Solar coral, summer without a filter',
    kit: {
      lipstickNote: 'Coral loves warm and olive skin; on cool skin it can turn orange, so shift to a pinker coral (#e28068).',
      blushNote: 'Creamy peach coral - the liquid continuation of the lipstick.',
      eyeshadowNote: 'Warm bronze shimmer. It reflects the coral in the lip.'
    },
    rationale: {
      whyItWorks: 'Everything stays in the warm family. Bronze shimmer reflects the coral lipstick, and peach blush extends it across the face. Total warm monochrome with no fractures.',
      skinFit: 'Perfect on warm and olive skin. Cool skin should adapt the lipstick. Deep skin can keep every intensity.',
      occasionFit: 'June to September. Terrace aperitivo, beach, summer weddings. In winter, keep it in reserve.',
      avoid: 'Any cool tone: mauve, berry, cool pink. It destroys the warm story.'
    },
    cosmeticMap: {
      intent: 'Solar warm coral',
      focus: ['lips', 'cheeks'],
      paletteFamily: 'coral peach bronze',
      temperature: 'warm',
      contrast: 'medium',
      chroma: 'clear',
      skinDepthFit: 'warm-olive-deep',
      finishProfile: { skin: 'sunlit glow', lips: 'satin coral', eyes: 'warm bronze shimmer', cheeks: 'cream coral' },
      placement: { eyes: 'blended warm bronze', cheeks: 'peach drape toward the center of the face', lips: 'full satin coral' },
      counterBrief: ['cool mauve', 'cool berry', 'silver shimmer'],
      riskControls: ['cool undertone correction', 'avoid orange cast', 'preserve warmth']
    }
  },
  'berry-whisper': {
    tagline: 'Discreet berry, long conversation',
    kit: {
      lipstickNote: 'Berry works on everyone, but on very fair skin it can darken the face; apply sheer or shift to a lighter berry (#a35668).',
      blushNote: 'Light berry in a sheer veil. Never full strength.',
      eyeshadowNote: 'Matte chocolate plum. Depth without drama.'
    },
    rationale: {
      whyItWorks: 'Cool berry family across the whole face. Soft enough to talk for hours without the makeup announcing itself; the look assumes a long conversation.',
      skinFit: 'Medium cool is the sweet spot. On fair skin dilute it; on deep skin keep the depth.',
      occasionFit: 'Intimate dinner, discreet dates, evenings where the exchange matters. Low profile, high care.',
      avoid: 'Intense shimmer, bright red, gold. They break the whisper.'
    },
    cosmeticMap: {
      intent: 'Soft, sophisticated evening berry',
      focus: ['lips', 'eyes'],
      paletteFamily: 'soft berry plum',
      temperature: 'cool-neutral',
      contrast: 'medium',
      chroma: 'muted',
      skinDepthFit: 'medium-cool-deep',
      finishProfile: { skin: 'natural satin', lips: 'satin berry', eyes: 'matte plum brown', cheeks: 'sheer berry' },
      placement: { eyes: 'controlled plum depth', cheeks: 'diluted berry', lips: 'natural satin edge' },
      counterBrief: ['intense gold', 'bright red', 'coral'],
      riskControls: ['dilute on fair skin', 'avoid bruised cheek', 'keep shimmer off']
    }
  },
  'velvet-autumn': {
    tagline: 'Velvet terracotta, October in Milan',
    kit: {
      lipstickNote: 'Terracotta loves olive and warm skin; on cool skin shift to a rosier terracotta (#a05448).',
      blushNote: 'Warm matte terracotta - brick, not coral.',
      eyeshadowNote: 'Matte copper brown. Earth on the eye.'
    },
    rationale: {
      whyItWorks: 'A coherent brick-earth palette. Everything matte for maximum color impact: no scatter, no light drift. The look reads like a painting.',
      skinFit: 'Warm, olive and deep. Cool skin should adapt the lipstick.',
      occasionFit: 'October and November, autumn evenings, creative outings, editorial looks.',
      avoid: 'Cool pink, gloss, cold shimmer. They break the earth.'
    },
    cosmeticMap: {
      intent: 'Editorial velvet terracotta',
      focus: ['lips', 'cheeks'],
      paletteFamily: 'terracotta brick',
      temperature: 'warm-earthy',
      contrast: 'medium-high',
      chroma: 'rich-muted',
      skinDepthFit: 'warm-olive-deep',
      finishProfile: { skin: 'velvet matte', lips: 'matte brick', eyes: 'matte copper brown', cheeks: 'matte terracotta' },
      placement: { eyes: 'matte earth on mobile lid and crease', cheeks: 'high diffused brick', lips: 'defined matte edge' },
      counterBrief: ['cool pink', 'gloss', 'cold shimmer'],
      riskControls: ['avoid muddy brown', 'cap cheek opacity', 'warm-only harmony']
    }
  },
  'wine-oclock': {
    tagline: 'Aperitivo burgundy',
    kit: {
      lipstickNote: 'Burgundy needs some skin depth. On pale fair skin it can age the face; prefer a rosier version (#8a3a48).',
      blushNote: 'Diluted berry wine. A wine shadow, not an accent.',
      eyeshadowNote: 'Dark matte plum. Structure for the eye.'
    },
    rationale: {
      whyItWorks: 'A continuous wine family. Satin lipstick gives refinement, matte eye gives structure. Different finishes, same color temperature.',
      skinFit: 'Medium, olive and deep. Fair skin should adapt.',
      occasionFit: 'Evening aperitivo, dinner, birthdays, the hour when the light drops.',
      avoid: 'Gloss, coral, bronze. Burgundy wants to cool the face, not warm it.'
    },
    cosmeticMap: {
      intent: 'Refined evening burgundy',
      focus: ['lips'],
      paletteFamily: 'wine burgundy',
      temperature: 'cool',
      contrast: 'high',
      chroma: 'deep-muted',
      skinDepthFit: 'medium-olive-deep',
      finishProfile: { skin: 'natural satin', lips: 'satin wine', eyes: 'matte dark plum', cheeks: 'sheer wine' },
      placement: { eyes: 'dark but low structure', cheeks: 'minimal wine shadow', lips: 'controlled edge' },
      counterBrief: ['coral', 'bronze', 'excessive gloss'],
      riskControls: ['adapt on fair skin', 'preserve lip dimension', 'avoid flat dark lip']
    }
  },
  'noir-classique': {
    tagline: 'Deep plum, head held high',
    kit: {
      lipstickNote: 'Very dark plum is a statement. Fair: shift to a lighter plum (#6c2a40). Olive/deep: keep the depth.',
      blushNote: 'Dark matte berry in a veil. Shadow-cheek.',
      eyeshadowNote: 'Matte plum smokey. Depth equal to the lip.'
    },
    rationale: {
      whyItWorks: 'Full-depth monochrome. Everything sinks into plum, and the face becomes one dramatic element. Serious drama, not commercial, not pop.',
      skinFit: 'Medium to deep preferred. On fair skin adapt it; otherwise it can read unintentionally gothic.',
      occasionFit: 'Event, theater, high-impact evening. Not casual, not office.',
      avoid: 'Any point of light: gloss, shimmer, highlighter. It breaks the gravity of the look.'
    },
    cosmeticMap: {
      intent: 'Deep, adult, dramatic plum',
      focus: ['lips', 'eyes'],
      paletteFamily: 'deep plum',
      temperature: 'cool',
      contrast: 'very-high',
      chroma: 'deep',
      skinDepthFit: 'medium-deep',
      finishProfile: { skin: 'soft matte', lips: 'matte deep plum', eyes: 'matte smokey plum', cheeks: 'matte sheer berry' },
      placement: { eyes: 'low blended smokey', cheeks: 'shadow-cheek', lips: 'full edge without flattening' },
      counterBrief: ['visible highlighter', 'gloss', 'coral'],
      riskControls: ['cap dark coverage', 'restore lip luminosity', 'avoid cadaveric blush']
    }
  },
  regina: {
    tagline: 'Classic red, does not ask permission',
    kit: {
      lipstickNote: 'Classic neutral red. On cool skin shift blue-red (#b01f2a), on warm skin shift tomato (#c33524).',
      blushNote: 'Warm peach - balances the coolness of the red without adding severity.',
      eyeshadowNote: 'Matte warm neutral. The eye stays back; the lipstick is the only protagonist.'
    },
    rationale: {
      whyItWorks: 'The matte red dominates. Eye and cheek must stay quiet: no shimmer, no competition. Warm peach blush offsets the red and keeps the face human.',
      skinFit: 'Works on every complexion; adaptation is only the exact red direction.',
      occasionFit: 'Important evenings, anniversaries, moments when you want to communicate confidence without explaining yourself. Not an office look.',
      avoid: 'Gloss, eye shimmer, visible highlighter, smokey. Any bright element breaks the archetype.'
    },
    cosmeticMap: {
      intent: 'Classic red as the protagonist',
      focus: ['lips'],
      paletteFamily: 'classic red / warm neutral',
      temperature: 'neutral',
      contrast: 'high',
      chroma: 'clear',
      skinDepthFit: 'universal',
      finishProfile: { skin: 'natural polished', lips: 'matte classic red', eyes: 'matte warm neutral', cheeks: 'satin peach' },
      placement: { eyes: 'recessed eye, neutral crease', cheeks: 'warm peach to keep the face alive', lips: 'defined red edge' },
      counterBrief: ['smokey', 'eye shimmer', 'visible highlighter'],
      riskControls: ['lip border precision', 'keep eyes quiet', 'undertone red shift']
    }
  },
  malizia: {
    tagline: 'Red that knows what it is doing',
    kit: {
      lipstickNote: 'Glossy deep red. On cool skin shift to blue-red (#7e1725); on warm skin keep this direction.',
      blushNote: 'Bright warm peach - satin, not matte. The face has to glow.',
      eyeshadowNote: 'Rose-gold shimmer. It amplifies the lipstick glow.'
    },
    rationale: {
      whyItWorks: 'Glossy red is spectacle. Rose-gold shimmer on the eye amplifies the glow, and satin blush connects it. It is not matte power like Regina; it is red carpet, light on light.',
      skinFit: 'Universal; adaptation is only the red direction.',
      occasionFit: 'An evening when you want to shine, parties, photo events, dinners where lights matter. It wants to be photographed.',
      avoid: 'All-over matte, a lightless nude eye, dull skin. Malizia needs reflections; denying them kills it.'
    },
    cosmeticMap: {
      intent: 'Glossy glamour red, light on light',
      focus: ['lips', 'skin'],
      paletteFamily: 'deep glossy red / rose gold',
      temperature: 'neutral-warm',
      contrast: 'high',
      chroma: 'rich',
      skinDepthFit: 'universal',
      finishProfile: { skin: 'glow satin', lips: 'glossy deep red', eyes: 'rose-gold shimmer', cheeks: 'satin peach' },
      placement: { eyes: 'rose gold on mobile lid with soft crease', cheeks: 'diffused satin glow', lips: 'controlled glossy red' },
      counterBrief: ['all-over matte', 'dull skin', 'nude eye without light'],
      riskControls: ['avoid sticker shimmer', 'cap gloss highlight', 'crease fade required']
    }
  },
  'dark-gothic': {
    tagline: 'Matte black, no concessions',
    kit: {
      lipstickNote: 'Cold black-plum, not pure black, to avoid a tattoo effect. Choose skin with enough contrast.',
      blushNote: 'Barely-there cool pink. Prevents a cadaverous effect.',
      eyeshadowNote: 'Blended matte black smokey. Continuity with the lip.'
    },
    rationale: {
      whyItWorks: 'The black lip is the absolute center. Very cool blush prevents a cadaverous effect, and black smokey creates vertical continuity on the face. Total look, zero compromise.',
      skinFit: 'Medium-light to deep. On very fair skin it can become teen emo; keep it only if that is intentional.',
      occasionFit: 'Alternative concerts, niche events, a night when normal is not an option. Halloween if it really fits.',
      avoid: 'Any warm tone: coral, peach, gold, bright blush. It breaks the code of the look.'
    },
    cosmeticMap: {
      intent: 'Alternative black-plum statement',
      focus: ['lips', 'eyes'],
      paletteFamily: 'black plum',
      temperature: 'cool',
      contrast: 'extreme',
      chroma: 'deep-muted',
      skinDepthFit: 'medium-light-deep',
      finishProfile: { skin: 'natural matte', lips: 'matte black plum', eyes: 'matte black plum smoke', cheeks: 'cold sheer pink' },
      placement: { eyes: 'blended smokey close to the lash line', cheeks: 'minimal anti-cadaverous veil', lips: 'full dark edge with visible texture' },
      counterBrief: ['gold', 'warm peach', 'bright blush'],
      riskControls: ['cap black opacity', 'restore lip shape', 'protect brow bridge']
    }
  }
};

function copyFor(style: MakeupStyle, lang: VtoLang): LocalizedStyleCopy | null {
  if (lang === 'it') return null;
  return en[style.id] ?? null;
}

function localizeKit(kit: StyleKit | undefined, copy: LocalizedKit | undefined): StyleKit | undefined {
  if (!kit || !copy) return kit;
  return {
    ...kit,
    lipstickNote: copy.lipstickNote,
    blush: { ...kit.blush, note: copy.blushNote },
    eyeshadow: { ...kit.eyeshadow, note: copy.eyeshadowNote }
  };
}

export function localizeStyleForDisplay(style: MakeupStyle, lang: VtoLang): MakeupStyle {
  const localized = copyFor(style, lang);
  if (!localized) return style;
  return {
    ...style,
    tagline: localized.tagline,
    kit: localizeKit(style.kit, localized.kit),
    rationale: localized.rationale ?? style.rationale,
    cosmeticMap: localized.cosmeticMap ?? style.cosmeticMap
  };
}
